import classNames from 'classnames';
import { createStyles } from '@jwp/ott-common/src/modules/container';
import { MediaStatus } from '@jwp/ott-common/src/utils/liveEvent';
import type { EpgChannel } from '@jwp/ott-common/types/epg';
import { useTranslation } from 'react-i18next';
import Image from '@jwp/ott-ui-react/src/components/Image/Image';
import Icon from '@jwp/ott-ui-react/src/components/Icon/Icon';
import Today from '@jwp/ott-theme/assets/icons/today.svg?react';
import Clock from '@jwp/ott-theme/assets/icons/clock.svg?react';
import Live from '@jwp/ott-theme/assets/icons/live_24dp.svg?react';
import Play from '@jwp/ott-theme/assets/icons/play.svg?react';
import Scheduled from '@jwp/ott-theme/assets/icons/scheduled.svg?react';
import type { FunctionComponent, SVGProps } from 'react';

import baseStyles from './EventTags.module.scss';

export const EventTagsIdentifier = Symbol(`EVENT_TAGS`);

const iconsMap = {
  date: Today,
  time: Clock,
  replay: Play,
};

type Props = {
  mediaStatus?: MediaStatus;
  channel?: EpgChannel;
  attributes: { id: string; value: string; iconOverride?: FunctionComponent<SVGProps<SVGSVGElement>> }[];
  small?: boolean;
  className?: string;
  showLiveTagOnly?: boolean;
};

const EventTags = ({ attributes, mediaStatus, channel, small, className, showLiveTagOnly = true }: Props) => {
  const { t } = useTranslation('common');
  const styles = createStyles(EventTagsIdentifier, baseStyles);

  const baseAttributes = attributes.map(({ id, value, iconOverride }) => {
    const icon = iconsMap[id as keyof typeof iconsMap];
    return { icon: iconOverride || icon ? <Icon icon={iconOverride || icon} className={styles.icon} /> : null, value };
  });

  const attributesList: { icon: React.ReactNode; value: string; className?: string }[] = [
    ...(mediaStatus === MediaStatus.LIVE ? [{ icon: <Icon icon={Live} className={styles.icon} />, value: t('live'), className: styles.live }] : []),
    ...(mediaStatus === MediaStatus.SCHEDULED && !showLiveTagOnly ? [{ icon: <Icon icon={Scheduled} className={styles.icon} />, value: t('scheduled') }] : []),
    ...baseAttributes,
    ...(channel ? [{ icon: channel.channelLogoImage, value: channel.title }] : []),
  ];

  const tags = attributesList.map(({ value, icon, className }) => (
    <div className={classNames(styles.tag, { [styles.small]: small }, className)} key={value}>
      {typeof icon === 'string' ? <Image image={icon} alt="" className={styles.icon} /> : icon}
      {value}
    </div>
  ));

  if (!tags.length) {
    return null;
  }

  return <div className={classNames(styles.tagContainer, className)}>{tags}</div>;
};

export default EventTags;
